/**
 * MarketDataTab — Shows aggregate market data cache summary.
 * Reuses getDataCacheStats() from the existing admin data-cache endpoint.
 */

import { useState, useEffect, useCallback } from 'react';
import { getDataCacheStats, type DataCacheStats } from '../../api/client.js';

// ============================================================================
// Helpers
// ============================================================================

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

// ============================================================================
// Spinner
// ============================================================================

function Spinner({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ============================================================================
// SummaryCard
// ============================================================================

interface SummaryCardProps {
  label: string;
  value: string;
}

function SummaryCard({ label, value }: SummaryCardProps) {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
      <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
      <div className="text-sm text-gray-400 mt-1">{label}</div>
    </div>
  );
}

// ============================================================================
// MarketDataTab
// ============================================================================

export function MarketDataTab() {
  const [stats, setStats] = useState<DataCacheStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDataCacheStats();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cache stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400 gap-3">
        <Spinner className="w-5 h-5" />
        <span>Loading market data stats...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <p className="text-red-400">{error}</p>
        <button
          onClick={() => { void fetchStats(); }}
          className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-200 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Note */}
      <div className="flex items-start gap-2 px-4 py-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-xs text-blue-300">
          Aggregate totals — not attributed per user. Market data is shared infrastructure.
          For per-dataset details and gap health, see the Data Cache page.
        </p>
      </div>

      {stats && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <SummaryCard label="Candles" value={formatNumber(stats.summary.totalCandles)} />
            <SummaryCard label="Funding Rates" value={formatNumber(stats.summary.totalFundingRates)} />
            <SummaryCard label="Open Interest" value={formatNumber(stats.summary.totalOpenInterest)} />
            <SummaryCard label="L/S Ratio" value={formatNumber(stats.summary.totalLongShortRatio)} />
            <SummaryCard label="Alt Metrics" value={formatNumber(stats.summary.totalAltMetrics)} />
            <SummaryCard label="Liquidations" value={formatNumber(stats.summary.totalLiquidations)} />
            <SummaryCard label="DB Size" value={formatBytes(stats.summary.dbSizeBytes)} />
          </div>

          {/* Per-symbol candle summary table */}
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-white">Candle Coverage by Symbol</h3>
              <button
                onClick={() => { void fetchStats(); }}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg text-gray-200 transition-colors disabled:opacity-50"
              >
                {loading ? <Spinner /> : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
                Reload
              </button>
            </div>

            <div className="overflow-x-auto max-h-80">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-900 border-b border-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Symbol</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">TF</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Count</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">From</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">To</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Exchange</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.candles.map((row, i) => (
                    <tr key={`${row.exchange}-${row.symbol}-${row.timeframe}`} className={i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}>
                      <td className="px-3 py-2 text-gray-100">{row.symbol}</td>
                      <td className="px-3 py-2 text-gray-300">{row.timeframe}</td>
                      <td className="px-3 py-2 text-gray-300 tabular-nums">{formatNumber(row.count)}</td>
                      <td className="px-3 py-2 text-gray-400 tabular-nums">{row.earliest.slice(0, 10)}</td>
                      <td className="px-3 py-2 text-gray-400 tabular-nums">{row.latest.slice(0, 10)}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{row.exchange}</td>
                    </tr>
                  ))}
                  {stats.candles.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-gray-500">No candle data</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
